import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { Product } from '@domain/product'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'

/**
 * A confirmação de tirar um produto do catálogo — e ela pergunta **duas coisas
 * diferentes**, conforme o produto.
 *
 * ## Com histórico, arquiva. Sem histórico, apaga.
 *
 * É a invariante de `Product.md`: *"produto com histórico não é apagado, só
 * arquivado"*. O histórico de preço é o dado que este app existe para
 * acumular; apagar o produto deixaria pontos apontando para um id que não
 * existe mais, junto com o detalhamento de qualquer nota que o tenha citado.
 *
 * **Quem decide é esta tela, e não a regra.** `firestore.rules` diz por
 * escrito que checar "existe histórico" custaria uma leitura extra a cada
 * delete, e por isso ela só garante posse: *"a disciplina de arquivar em vez
 * de apagar um produto com histórico é da aplicação"*. A página do produto já
 * carregou o histórico para desenhar a tela — ela sabe, sem ler nada a mais.
 *
 * ## E o texto MUDA junto, porque as duas coisas são diferentes
 *
 * Um "tem certeza?" igual para os dois casos deixaria a pessoa achar que
 * perdeu o histórico quando ele ficou, ou que ele ficou quando o produto sumiu
 * de vez. O verbo do botão muda com o texto: "Arquivar" e "Apagar" não são
 * sinônimos aqui.
 *
 * ## Confirmar em folha, e não em `window.confirm`
 *
 * O diálogo do navegador não tem a tipografia nem as cores do app, não dá para
 * explicar a diferença entre arquivar e apagar em duas linhas, e num iPhone
 * instalado ele aparece com o nome do domínio em cima. A folha é a mesma que o
 * resto do app usa para perguntar.
 */
/**
 * A frase dos preços que ficam, com a concordância certa.
 *
 * Montada inteira por caso, e não emendando um número num molde só: a primeira
 * versão interpolava e saía *"Os 1 preço registrado continua guardados"*, que é
 * o defeito clássico de tratar plural como sufixo.
 */
function guardados(quantos: number): string {
  return quantos === 1
    ? 'O preço já registrado continua guardado'
    : `Os ${String(quantos)} preços já registrados continuam guardados`
}

export function ConfirmarApagar({
  produto,
  pontos,
  onFechar,
}: {
  produto: Product
  /** Quantos pontos de preço existem. É isto que decide arquivar ou apagar. */
  pontos: number
  onFechar: () => void
}) {
  const { productRepository } = useFinanceRepositories()
  const navegar = useNavigate()
  const [indo, setIndo] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const temHistorico = pontos > 0

  const confirmar = () => {
    setIndo(true)
    setErro(null)
    const acao = temHistorico
      ? productRepository.archive(produto.id)
      : productRepository.remove(produto.id)

    void acao
      .then(() => {
        // Volta para o catálogo, e não para o histórico: a tela de onde se
        // veio pode ser a deste produto, que acabou de deixar de existir.
        void navegar({ to: '/produtos' })
      })
      .catch((e: unknown) => {
        console.error('ConfirmarApagar: não deu para tirar o produto', e)
        setErro('não foi possível agora')
        setIndo(false)
      })
  }

  return (
    <FolhaDeBaixo
      titulo={temHistorico ? 'Arquivar o produto' : 'Apagar o produto'}
      onFechar={onFechar}
    >
      <p className="mb-1 text-sm font-medium">{produto.name}</p>

      <p className="text-sm text-[var(--text-muted)]">
        {temHistorico
          ? `Ele sai das listas e das buscas. ${guardados(pontos)} — é o histórico que faz este app valer alguma coisa, e ele não se apaga.`
          : 'Ele nunca teve preço registrado, então some de vez. Não dá para desfazer.'}
      </p>

      <div className="mt-4 flex items-center gap-2">
        <Botao tom="perigo" type="button" disabled={indo} onClick={confirmar}>
          {temHistorico ? 'Arquivar' : 'Apagar'}
        </Botao>
        <Botao type="button" onClick={onFechar}>
          Cancelar
        </Botao>
      </div>

      {erro !== null && (
        <p role="alert" className="mt-2 text-sm text-[var(--negative)]">
          {erro}
        </p>
      )}
    </FolhaDeBaixo>
  )
}
